import {
  Alert,
  Box,
  Container,
  Header,
  Link,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";
import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LandingPage from "./LandingPage";
import { OrderIdContext, StageContext } from "../contexes/OrderIdContext";
import useFetchDetails from "../hooks/useFetchDetails";
import useFetchDlqInfo from "../hooks/useFetchDlqInfo";
import useFetchMatchSets from "../hooks/useFetchMatchSets";
import { useMarketplaceId } from "../contexes/MarketplaceIdContext";
import Results from "./events/Results";
import {
  AccountingEventsGroup,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../types";
import Summary from "./summary/Summary";
import ErrorAlert from "./common/ErrorAlert";
import DlqStatusIndicator from "./dlq/DlqStatusIndicator";
import { PERMISSION_LINK } from "../constants";
import { getEventLogGroups } from "../helpers/logUtils";
import EventButtons from "./common/EventButtons";
import { AnalyticsContext } from "./dashboard/AnalyticsProvider";
import { MatchSetsProvider } from "../contexes/MatchSetsContext";

function AuidDisambiguation({
  auids,
  orderId,
  stage,
}: {
  auids: string[];
  orderId: string;
  stage: string;
}) {
  return (
    <Container
      header={
        <Header variant="h2">
          Multiline Order — Multiple AUIDs Found
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="s">
        <Box>
          Order <b>{orderId}</b> is a multiline order with multiple associated
          AUIDs. Select one to view its data:
        </Box>
        {auids.map((auid) => (
          <Box key={auid}>
            <Link
              href={`?stage=${encodeURIComponent(stage)}&transactionId=${encodeURIComponent(auid)}`}
            >
              {auid}
            </Link>
          </Box>
        ))}
      </SpaceBetween>
    </Container>
  );
}

export default function MainDataPage() {
  const orderId = useContext(OrderIdContext);
  const stage = useContext(StageContext);
  const [, setSearchParams] = useSearchParams();
  const [showFallbackAlert, setShowFallbackAlert] = useState(false);
  const [fallbackMessage, setFallbackMessage] = useState<string>("");

  const {
    data: detailsData,
    error: detailsError,
    isFetching: detailsFetching,
    isLoading: detailsLoading,
  } = useFetchDetails(stage, orderId);

  const dlqInfo = useFetchDlqInfo(stage, orderId);

  const { marketplaceId } = useMarketplaceId();
  const matchSetData = useFetchMatchSets(
    stage,
    detailsData?.cbes || [],
    marketplaceId,
    !!detailsData && (detailsData.cbes?.length ?? 0) > 0
  );

  // Update URL, searh history, and page title when fallback occurs
  useEffect(() => {
    if (detailsData?.fallback) {
      const resolvedId = detailsData.fallback.resolvedId || "";

      const message =
        detailsData.fallback.type === "subscriptionId"
          ? `Resolved subscription ID to AUID: ${resolvedId}`
          : `Showing results for related order ${detailsData.fallback.originalId ?? ""}`;
      setFallbackMessage(message);
      setShowFallbackAlert(true);

      setSearchParams(
        {
          stage,
          transactionId: resolvedId,
        },
        { replace: true }
      );

      setTimeout(() => {
        const existingEntries: { orderId: string; stage: string }[] =
          JSON.parse(localStorage.getItem("orderIds") || "[]") as {
            orderId: string;
            stage: string;
          }[];
        const filteredEntries = existingEntries.filter(
          (entry) =>
            !(
              entry.orderId === detailsData.fallback?.originalId &&
              entry.stage === stage
            )
        );
        localStorage.setItem("orderIds", JSON.stringify(filteredEntries));
      }, 100);

      document.title = `${resolvedId} | Lusca Search`;
    }
  }, [detailsData?.fallback, stage, setSearchParams]);

  // Show alert if we detected a fallback
  const shouldShowFallbackAlert = showFallbackAlert;

  const isMultilineOrder =
    detailsData?.auids && detailsData.auids.length > 1;

  const isEventAndCbeEmpty =
    detailsData?.events.length === 0 && detailsData?.cbes.length === 0 && !isMultilineOrder;

  const isLoadingData = detailsLoading;
  const isFetchingData = detailsFetching;
  const isError = detailsError !== null;

  const { recordError } = useContext(AnalyticsContext);
  useEffect(() => {
    if (detailsError) {
      console.log("Caught error: ", detailsError);
      recordError(detailsError);
    }
  }, [detailsError, recordError]);

  return (
    <div>
      {isLoadingData && !isFetchingData ? (
        <LandingPage />
      ) : isFetchingData ? (
        <Box textAlign="center">
          <Spinner size="big" />
        </Box>
      ) : isError ? (
        <ErrorAlert
          header="An error occurred while fetching the data"
          content={
            <>
              Could not fetch the data from server. You might not have{" "}
              <a href={PERMISSION_LINK}>permission</a> to use this tool. (
              {detailsError.message})
            </>
          }
        />
      ) : isMultilineOrder ? (
        <AuidDisambiguation
          auids={detailsData!.auids!}
          orderId={orderId}
          stage={stage}
        />
      ) : isEventAndCbeEmpty ? (
        <ErrorAlert
          header="Could not find any events or CBEs with given id"
          content={
            <SpaceBetween size="s">
              <div>
                Transaction with the given order id might not exist, or may have
                DLQ items.
              </div>
              <div>
                DLQ Status:{" "}
                <DlqStatusIndicator
                  status={dlqInfo.status}
                  dlqItems={dlqInfo.dlqItems}
                  stage={stage}
                />
              </div>
              <EventButtons
                identifier={orderId}
                stage={stage}
                logGroups={getEventLogGroups(stage)}
                dateRange={[new Date(Date.now() - 60 * 60 * 1000), new Date()]} // Last hour
              />
            </SpaceBetween>
          }
        />
      ) : (
        <SpaceBetween size="xl">
          {shouldShowFallbackAlert && (
            <Alert type="info" header={fallbackMessage} />
          )}
          <MatchSetsProvider matchSets={matchSetData.matchSets} isLoading={matchSetData.isLoading}>
            <SpaceBetween size="xl">
              <Summary
                eventData={
                  (detailsData?.events || []) as (
                    | AccountingEventsGroup
                    | DigitalBusinessDocument
                    | DigitalCostDocument
                    | DigitalPaymentDocument
                  )[]
                }
                dlqInfo={dlqInfo}
                cbeData={detailsData?.cbes || []}
                matchSetData={matchSetData}
              />
              <Results />
            </SpaceBetween>
          </MatchSetsProvider>
        </SpaceBetween>
      )}
    </div>
  );
}
